/**
 * @interface UserTraits
 * Represents the traits of a user for integration with SatisMeter.
 */
interface UserTraits {
  /** The name of the user (optional). */
  name?: string
  /** The email address of the user (optional). */
  email?: string
  /** The date when the user was created, typically in ISO 8601 format (optional). */
  createdAt?: string
  /** The role of the user (optional). */
  userRole?: string
  /** The language of the user (optional). */
  language?: string
  /** The country of the user (optional). */
  country?: string
}

/**
 * @interface SatisMeterConfig
 * Configuration options for initializing SatisMeter.
 */
interface SatisMeterConfig {
  /** The unique key used to authenticate requests to SatisMeter. */
  writeKey: string
  /** The identifier for the user being tracked. */
  userId: string
  /** Optional additional information about the user, such as name, email, or other custom attributes. */
  traits?: UserTraits
}

// Extend the global window interface to include SatisMeter
declare global {
  interface Window {
    satismeter?: {
      (config: SatisMeterConfig): void
      q?: unknown[]
      l?: number
    }
  }
}

// Constants for SatisMeter configuration
const SATISMETER_WRITE_KEY = process.env.REACT_APP_SATISMETER_WRITE_KEY
const SATISMETER_SCRIPT_URL = process.env.REACT_APP_SATISMETER_SCRIPT_URL

/**
 * Initializes the SatisMeter feedback tool by loading its script and configuring it with user data.
 * 
 * @example
 * const userId = "12345";
 * const traits = { name: "Jane Doe", email: "janedoe@example.com", createdAt: "2024-10-01T12:00:00Z" };
 * initSatisMeter(userId, traits);
 */
export function initSatisMeter(userId: string, traits: UserTraits): void {
  // Validate the write key
  if (!SATISMETER_WRITE_KEY) {
    console.error('SATISMETER_WRITE_KEY is not defined')
    return
  }

  // Validate the script URL
  if (!SATISMETER_SCRIPT_URL) {
    console.error('SATISMETER_SCRIPT_URL is not defined')
    return
  }

  // Validate the user ID
  if (!userId) {
    console.error('User ID is required to initialize SatisMeter')
    return
  }

  try {
    // Ensure the SatisMeter script is loaded
    if (!window.satismeter) {
      window.satismeter = function () {
        if (window.satismeter && window.satismeter.q) {
          (window.satismeter.q = window.satismeter.q || []).push(arguments)
        }
      }

      window.satismeter.l = Date.now()

      const script = document.createElement('script')
      Object.assign(script, { async: true, src: SATISMETER_SCRIPT_URL })
      document.body.appendChild(script)

      // Wait for the script to load before initializing SatisMeter
      script.onload = () => {
        if (window.satismeter) {
          window.satismeter({
            writeKey: SATISMETER_WRITE_KEY,
            userId, // Current user unique ID (required)
            traits, // User traits (optional)
          })
        } else {
          console.error('SatisMeter is not available after script load.')
        }
      }

      script.onerror = () => {
        console.error('Failed to load SatisMeter script.')
      }
    } else {
      // If SatisMeter is already loaded, initialize it with the provided user data
      window.satismeter({
        writeKey: SATISMETER_WRITE_KEY,
        userId, // Current user unique ID (required)
        traits, // User traits (optional)
      })
    }
  } catch (error) {
    console.error('Error initializing SatisMeter:', error)
  }
}
